import PageButton from "@/components/atoms/pagination/PageButton";
import { Select } from "@/components/atoms/inputForms/Select";

import ChevronIcon from "public/icon/chevron-right-new.svg";

interface Props {
  current: number;
  max: number;
  pushPage: (page: number, perPage?: number) => Promise<void> | void;
  perPage?: number;
  total?: number;
  delta?: number;
}

type Direction = "next" | "before";

interface ChevronProps {
  direction: Direction;
  onClick?: () => void | Promise<void>;
}

const PER_PAGE_OPTIONS = [
  {
    value: 15,
    text: "15",
  },
  {
    value: 25,
    text: "25",
  },
  {
    value: 50,
    text: "50",
  },
  {
    value: 100,
    text: "100",
  },
];

const DEFAULT_PER_PAGE = PER_PAGE_OPTIONS[2].value; // 50

function Chevron({ direction, onClick }: ChevronProps) {
  const size = 24;
  return direction === "next" ? (
    <ChevronIcon
      onClick={onClick}
      width={size}
      height={size}
      className={`fill-primary`}
      title="次のページへ"
    />
  ) : (
    <ChevronIcon
      onClick={onClick}
      width={size}
      height={size}
      className={`fill-primary scale-x-[-1]`}
      title="前のページへ"
    />
  );
}

/**
 * ページ番号・前へ・次へボタン
 */
export default function Pagination({
  current,
  max,
  pushPage,
  perPage = DEFAULT_PER_PAGE,
  total,
  delta = 1,
}: Props) {
  const getPerPageValue = () => {
    const index = PER_PAGE_OPTIONS.findIndex((el) => el.value === perPage);
    if (index < 0) {
      return PER_PAGE_OPTIONS[2].value;
    }
    return PER_PAGE_OPTIONS[index].value;
  };

  const pushBefore = async () => {
    if (current <= 1) {
      throw new Error("現在ページが1ページ未満です");
    }
    await pushPage(current - 1, +perPage);
  };

  const pushNext = async () => {
    if (current >= max) {
      throw new Error("現在ページが最大ページです");
    }
    await pushPage(current + 1, +perPage);
  };

  const range: number[] = [];
  const displayItems: JSX.Element[] = [];

  for (let i = 1; i <= max; i++) {
    if (
      i === 1 ||
      i === max ||
      (i >= current - delta && i <= current + delta)
    ) {
      range.push(i);
    }
  }

  let prevPage = 0;

  for (const page of range) {
    if (prevPage) {
      if (page - prevPage === 2) {
        // pushPageにprevPage + 1を直接代入してしまうとpushPage()が発火したタイミングでのprevPageが参照されてしまうためバグる
        const nextPage = prevPage + 1;
        displayItems.push(
          <li key={prevPage + 1}>
            <PageButton
              isActive={prevPage + 1 === current}
              onClick={async () => pushPage(nextPage, +perPage)}
            >
              {prevPage + 1}
            </PageButton>
          </li>,
        );
      } else if (page - prevPage > 2) {
        displayItems.push(
          <li key={prevPage + 1} className="text-theme-medium">
            ･･･
          </li>,
        );
      }
    }
    displayItems.push(
      <li key={page}>
        <PageButton
          isActive={page === current}
          onClick={async () => pushPage(page, +perPage)}
        >
          {page}
        </PageButton>
      </li>,
    );
    prevPage = page;
  }

  const getCurrentContentRange = (): string => {
    if (!perPage || !total) {
      return "0";
    }

    if (perPage >= total) {
      return `${current}〜${total}`;
    }
    // 最初のページかつ、トータルページがperPageより多い
    if (current === 1) {
      return `${current}〜${perPage}`;
    }

    // ページの
    const showingCount = (current - 1) * perPage + 1;
    // 終端の件数が合計件数を上回る場合は合計件数を表示
    const endCount = current * perPage > total ? total : current * perPage;
    return `${showingCount}〜${endCount}`;
  };

  return (
    <ul className="flex flex-wrap gap-2 justify-center items-center md:flex-nowrap md:justify-start">
      <li className="flex items-center">
        <label className="md:mb-0 mt-1 inline-block text-xs font-bold text-[#1A1A1C] mr-4">
          表示件数
        </label>
        <Select
          onChange={(perPage) => {
            void pushPage(1, +perPage);
          }}
          options={PER_PAGE_OPTIONS}
          value={getPerPageValue()}
          size="sm"
          // className="h-9"
          optionClassName="!px-2"
        />
      </li>
      {perPage && total && total !== 0 && (
        <li className="basis-full text-center md:basis-auto md:text-left">
          <span className="text-color-gray-700 text-xs font-bold leading-normal">
            {total}件中 {getCurrentContentRange()}件表示
          </span>
        </li>
      )}
      <li className="w-9 h-9">
        <button
          type="button"
          onClick={() => void pushBefore()}
          disabled={current === 1}
          className={`flex justify-center items-center w-full h-full rounded ${
            current === 1
              ? "opacity-30 cursor-not-allowed"
              : "hover:bg-primary-hover"
          }`}
        >
          <Chevron direction="before" />
        </button>
      </li>
      {displayItems}
      <li className="w-9 h-9">
        <button
          type="button"
          onClick={() => void pushNext()}
          disabled={current === max}
          className={`flex justify-center items-center w-full h-full rounded ${
            current === max
              ? "opacity-30 cursor-not-allowed"
              : "hover:bg-primary-hover"
          }`}
        >
          <Chevron direction="next" />
        </button>
      </li>
    </ul>
  );
}
